import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, Check, X, Stethoscope, BellRing, Plus } from "lucide-react";
import { toast } from "sonner";
import { AppointmentStatusSelect } from "@/components/common/AppointmentStatusSelect";
import { RescheduleButton } from "@/components/common/RescheduleButton";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const KIND_COLOR = { appointment: "#00A85A", followup: "#F59E0B", surgery: "#EF4444", consultation: "#0EA5E9" };
const FILTERS = [["all", "All"], ["appointment", "Appointments"], ["surgery", "Surgeries"], ["followup", "Follow-ups"]];
const TYPES = ["Consultation", "Follow-up", "Surgery", "Treatment"];

const dkey = (d) => (d || "").slice(0, 10);
const kindFromType = (type, procedure) => {
  const t = `${type || ""} ${procedure || ""}`.toLowerCase();
  if (t.includes("surgery")) return "surgery";
  if (t.includes("follow")) return "followup";
  if (t.includes("consult")) return "consultation";
  return "appointment";
};
const emptyForm = (date) => ({ patient_id: "", date: date || dkey(new Date().toISOString()), time: "09:00", type: "Consultation", procedure: "" });

export default function Appointments() {
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [patients, setPatients] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [filter, setFilter] = useState("all");
  const [dayOpen, setDayOpen] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/appointments").then((r) => setAppts(r.data)).catch(() => setAppts([]));
    api.get("/followups").then((r) => setFollowups(r.data)).catch(() => setFollowups([]));
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => setPatients([]));
  };
  useEffect(() => { load(); }, []);

  const openAdd = (date) => { setForm(emptyForm(date)); setAddOpen(true); };
  const saveAppointment = async () => {
    if (!form.patient_id) { toast.error("Please select a patient"); return; }
    setSaving(true);
    try {
      const p = patients.find((x) => x.id === form.patient_id);
      await api.post("/appointments", { ...form, patient_name: p?.name || "", status: "confirmed" });
      toast.success("Appointment scheduled");
      setAddOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule appointment"); }
    finally { setSaving(false); }
  };

  const events = useMemo(() => {
    const a = (appts || []).map((x) => ({ id: x.id, date: dkey(x.date), time: x.time, kind: kindFromType(x.type, x.procedure), label: x.type || "Appointment", sub: x.procedure, patient_name: x.patient_name, patient_id: x.patient_id, status: x.status, raw: x }));
    const f = (followups || []).map((x) => ({ id: x.id, date: dkey(x.due_date), time: "", kind: "followup", label: "Follow-up", sub: x.note, patient_name: x.patient_name, patient_id: x.patient_id, status: x.status }));
    return [...a, ...f].filter((e) => e.date && (filter === "all" || e.kind === filter));
  }, [appts, followups, filter]);

  const byDay = useMemo(() => { const m = {}; events.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); }); return m; }, [events]);

  const { y, m } = cursor;
  const first = new Date(y, m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const todayKey = dkey(new Date().toISOString());

  const dayEvents = dayOpen ? (byDay[dayOpen] || []) : [];

  return (
    <div>
      <PageHeader title="Appointments" subtitle="Calendar of consultations, surgeries & follow-ups" breadcrumb={["Practice", "Appointments"]} />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => setCursor(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 })} className="h-9 w-9 rounded-lg"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="w-44 text-center text-sm font-bold text-[#101516] dark:text-white">{MONTHS[m]} {y}</div>
          <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => setCursor(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 })} className="h-9 w-9 rounded-lg"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} className="h-9 rounded-lg">Today</Button>
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto items-center">
          {FILTERS.map(([k, l]) => <button key={k} data-testid={`cal-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === k ? "bg-[#00A85A] text-white" : "bg-black/5 text-[#4E5F68] dark:bg-white/10 dark:text-[#8B9698]"}`}>{l}</button>)}
          <Button size="sm" data-testid="new-appointment-btn" onClick={() => openAdd()} className="h-9 rounded-lg bg-[#00A85A] hover:bg-[#008F4D] text-white gap-1.5"><Plus className="h-4 w-4" />New appointment</Button>
        </div>
      </div>

      <SurfaceCard className="p-2 sm:p-3">
        <div className="grid grid-cols-7 mb-1">{DOW.map((d) => <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-[#8B9698] py-1">{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="aspect-square sm:aspect-[4/3] rounded-lg bg-black/[0.015] dark:bg-white/[0.02]" />;
            const key = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const evs = byDay[key] || [];
            const isToday = key === todayKey;
            return (
              <button key={i} data-testid={`cal-day-${d}`} onClick={() => evs.length ? setDayOpen(key) : openAdd(key)}
                className={`group aspect-square sm:aspect-[4/3] overflow-hidden rounded-lg border p-1.5 text-left transition-colors ${isToday ? "border-[#00A85A] bg-[#00A85A]/5" : "border-[#E2E8F0] dark:border-[#223139]"} hover:border-[#00A85A]/60 cursor-pointer`}>
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-xs font-bold tabular-nums ${isToday ? "text-[#00A85A]" : "text-[#101516] dark:text-white"}`}>{d}</span>
                  {evs.length > 0 ? <span className="rounded bg-[#00A85A]/15 px-1 text-[9px] font-bold text-[#008F4D] dark:text-[#3FD98A]">{evs.length}</span>
                    : <Plus className="h-3 w-3 text-[#8B9698] opacity-0 group-hover:opacity-100 transition-opacity" />}
                </div>
                {evs.length > 0 && (
                  <div className="mt-1 flex items-center -space-x-1.5 overflow-hidden">
                    {evs.slice(0, 3).map((e, x) => <Avatar key={x} name={e.patient_name || "?"} size={16} color={KIND_COLOR[e.kind]} className="ring-2 ring-white dark:ring-[#0B1A20]" />)}
                    {evs.length > 3 && <span className="ml-2 text-[9px] font-bold text-[#8B9698]">+{evs.length - 3}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </SurfaceCard>

      {events.length === 0 && <p className="mt-6 text-center text-sm text-[#8B9698]">No {filter === "all" ? "" : filter + " "}events this view. Click a day or use “New appointment” to schedule.</p>}

      <Dialog open={!!dayOpen} onOpenChange={(o) => !o && setDayOpen(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{dayOpen && new Date(dayOpen).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {dayEvents.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-3">
                <Avatar name={e.patient_name || "?"} size={36} color={KIND_COLOR[e.kind]} />
                <button onClick={() => e.patient_id && navigate(`/patients/${e.patient_id}`)} className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-bold text-[#101516] dark:text-white truncate">{e.patient_name || "Patient"}</div>
                  <div className="text-xs text-[#8B9698] truncate flex items-center gap-1">{e.kind === "followup" ? <BellRing className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}{e.time ? e.time + " · " : ""}{e.label}{e.sub ? " · " + e.sub : ""}</div>
                </button>
                {e.kind === "followup" ? <StatusBadge status={e.status} /> : <span className="flex flex-wrap items-center gap-2"><AppointmentStatusSelect appt={e} onChanged={() => load()} /><RescheduleButton appt={e} onDone={() => load()} /></span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-appointment-dialog">
          <DialogHeader><DialogTitle>New appointment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Patient</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm((f) => ({ ...f, patient_id: v }))}>
                <SelectTrigger data-testid="appt-patient-select"><SelectValue placeholder="Select a patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`appt-patient-${p.id}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" data-testid="appt-date-input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Time</Label>
                <Input type="time" data-testid="appt-time-input" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="appt-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`appt-type-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes <span className="text-[#8B9698] font-normal">(optional)</span></Label>
              <Input data-testid="appt-notes-input" placeholder="Procedure or note" value={form.procedure} onChange={(e) => setForm((f) => ({ ...f, procedure: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-lg">Cancel</Button>
              <Button data-testid="appt-save-btn" onClick={saveAppointment} disabled={saving} className="rounded-lg bg-[#00A85A] hover:bg-[#008F4D] text-white">{saving ? "Scheduling…" : "Schedule"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
